import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { useToast } from '../hooks/use-toast';
import api from '../services/api';
import FlashcardViewer from './FlashcardViewer';

const FlashcardSets = ({ onBack }) => {
  const [sets, setSets] = useState([]);
  const [selectedSet, setSelectedSet] = useState(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    fetchFlashcardSets();
  }, []);

  const fetchFlashcardSets = async () => {
    try {
      const response = await api.get('/flashcards/my-sets');
      setSets(response.data);
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to load flashcard sets",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  if (selectedSet) {
    return (
      <FlashcardViewer 
        setId={selectedSet.set_id}
        title={selectedSet.title}
        onBack={() => {
          setSelectedSet(null);
          // Refetch sets to get updated mastery percentages
          fetchFlashcardSets();
        }}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 via-blue-50 to-cyan-100 p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">My Flashcard Sets</h1>
            <p className="text-gray-600">Click on a set to start practicing</p>
          </div>
          <Button onClick={onBack} variant="outline">
            ← Back to Dashboard
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-purple-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading flashcard sets...</p>
          </div>
        ) : sets.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-gray-600 text-lg">No flashcard sets assigned to you yet.</p>
              <p className="text-gray-500 mt-2">Check back later or contact your administrator.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sets.map((set) => (
              <Card 
                key={set.set_id} 
                className="cursor-pointer hover:shadow-lg transition-shadow duration-200"
                onClick={() => setSelectedSet(set)}
              >
                <CardHeader>
                  <CardTitle className="text-xl">{set.title}</CardTitle>
                  {set.description && (
                    <CardDescription className="mt-2">{set.description}</CardDescription>
                  )}
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">
                        {set.card_count} {set.card_count === 1 ? 'card' : 'cards'}
                      </span>
                      <Button size="sm">
                        Start →
                      </Button>
                    </div>
                    
                    {/* Mastery Percentage */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600 font-semibold">Mastery</span>
                        <span className={`font-bold ${
                          set.mastery_percentage >= 80 ? 'text-green-600' :
                          set.mastery_percentage >= 50 ? 'text-yellow-600' :
                          'text-red-600'
                        }`}>
                          {set.mastery_percentage}%
                        </span>
                      </div>
                      
                      {/* Progress Bar */}
                      <div className="w-full bg-gray-200 rounded-full h-2.5">
                        <div 
                          className={`h-2.5 rounded-full transition-all duration-500 ${
                            set.mastery_percentage >= 80 ? 'bg-green-600' :
                            set.mastery_percentage >= 50 ? 'bg-yellow-500' :
                            'bg-red-500'
                          }`}
                          style={{ width: `${set.mastery_percentage}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FlashcardSets;
